// FilterBar (UI-LAW §4, §4b.4) — the ONE filter affordance. A resting,
// unfiltered list shows a single 32px `Filter` button and no other chrome; the
// button opens a `Popover` that drills facet name -> that facet's values, and
// every applied facet renders as an editable chip beside the trigger. Filtering
// applies the instant a value is toggled, so there is no Apply button; `Clear`
// appears after the last chip once one removable facet is applied.
//
// Composed from the ratified set: `Button`, `Popover`, `DateRangePicker`,
// `Text`, and a §4d `HelpTip` slot. No second popover, no new styling system,
// no dependency — `Popover` already owns `role="dialog"`, outside-click and
// Escape dismissal, and focus return.
//
// The applied VALUE is owned by the caller and belongs in the URL (§4c): every
// screen serializes each facet to a search param named after the facet id,
// multi-value facets comma-joined.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import { Button } from "./Button";
import { DateRangePicker, type DateRange } from "./DateRangePicker";
import { Popover } from "./Popover";
import { Text } from "./Typography";

/** One selectable value of a facet. `count` is the loaded-row occurrence. */
export interface FilterOption {
  count?: number;
  label: string;
  value: string;
}

/**
 * A facet the caller offers. `removable: false` renders the chip without an
 * `×` and keeps `Clear` from touching it — the agent-scope case, where the
 * sidebar selector owns the value (`AlignmentFilters`' `isAgentLocked` rule).
 */
export type FilterFacet =
  | {
      id: string;
      kind: "multi";
      label: string;
      options: FilterOption[];
      removable?: boolean;
    }
  | {
      id: string;
      kind: "single";
      label: string;
      options: FilterOption[];
      removable?: boolean;
    }
  | { id: string; kind: "dateRange"; label: string; removable?: boolean };

/** Applied filters, keyed by facet id. Absent / empty = not applied. */
export type FilterValue = Record<string, string[] | DateRange | undefined>;

export interface FilterBarProps {
  facets: FilterFacet[];
  /**
   * Names the rows behind `resultCount` for the polite announcement, e.g.
   * `(count) => (count === 1 ? "1 persona" : `${count} personas`)`. The caller
   * owns the copy because pluralization is not guessable.
   */
  formatResultCount?: (count: number) => string;
  /** A `HelpTip` beside the trigger (§4d). Only where semantics are unclear. */
  help?: ReactNode;
  /**
   * Muted note after the chips. A facet whose options are harvested from the
   * loaded page of a keyset list MUST pass "Loaded rows only" (§8 — never
   * present a partial narrowing as a whole-list one).
   */
  note?: ReactNode;
  onChange: (next: FilterValue) => void;
  /**
   * Rows matching the applied filters. Announced in a polite live region: the
   * table sits outside the popover's focus path, so an emptied list is
   * otherwise silent.
   */
  resultCount?: number;
  value: FilterValue;
}

const EMPTY_SELECTION: string[] = [];
const EMPTY_RANGE: DateRange = { end: null, start: null };

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="marker-filter-bar__check"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="m5 12 5 5 9-11" />
    </svg>
  );
}

/** A stored facet entry is a date range when it is a non-array object. */
export function isDateRangeValue(
  entry: string[] | DateRange | undefined,
): entry is DateRange {
  return entry !== undefined && !Array.isArray(entry);
}

function selectionOf(value: FilterValue, id: string): string[] {
  const entry = value[id];
  return Array.isArray(entry) ? entry : EMPTY_SELECTION;
}

function rangeOf(value: FilterValue, id: string): DateRange {
  const entry = value[id];
  return isDateRangeValue(entry) ? entry : EMPTY_RANGE;
}

function isApplied(facet: FilterFacet, value: FilterValue): boolean {
  if (facet.kind === "dateRange") {
    const range = rangeOf(value, facet.id);
    return range.start !== null || range.end !== null;
  }
  return selectionOf(value, facet.id).length > 0;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Chip text: `facet: value, value` with an implicit "is any of". */
function chipText(facet: FilterFacet, value: FilterValue): string {
  const name = facet.label.toLocaleLowerCase();
  if (facet.kind === "dateRange") {
    const range = rangeOf(value, facet.id);
    if (range.start !== null && range.end !== null) {
      return `${name}: ${formatDate(range.start)} to ${formatDate(range.end)}`;
    }
    if (range.start !== null) {
      return `${name}: from ${formatDate(range.start)}`;
    }
    if (range.end !== null) {
      return `${name}: until ${formatDate(range.end)}`;
    }
    return name;
  }
  const labels = selectionOf(value, facet.id).map(
    (entry) =>
      facet.options.find((option) => option.value === entry)?.label ?? entry,
  );
  return `${name}: ${labels.join(", ")}`;
}

function defaultResultCount(count: number): string {
  return `${String(count)} results`;
}

export function FilterBar({
  facets,
  formatResultCount = defaultResultCount,
  help,
  note,
  onChange,
  resultCount,
  value,
}: FilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  // null = the facet-name step; a facet id = that facet's value step.
  const [openFacetId, setOpenFacetId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const appliedFacets = useMemo(
    () => facets.filter((facet) => isApplied(facet, value)),
    [facets, value],
  );
  const appliedCount = appliedFacets.length;
  const canClear = appliedFacets.some((facet) => facet.removable !== false);
  const openFacet = facets.find((facet) => facet.id === openFacetId) ?? null;

  // Focus sync with the DOM: opening the panel, or drilling between its two
  // steps, moves focus onto the step's first row so the keyboard path never
  // lands on the panel container.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    panelRef.current?.querySelector<HTMLElement>("[data-filter-item]")?.focus();
  }, [isOpen, openFacetId]);

  const setFacet = useCallback(
    (id: string, next: string[] | DateRange | undefined) => {
      onChange({ ...value, [id]: next });
    },
    [onChange, value],
  );

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setOpenFacetId(null);
  }, []);

  const editFacet = useCallback((id: string) => {
    setOpenFacetId(id);
    setIsOpen(true);
  }, []);

  function clearAll(): void {
    const next: FilterValue = { ...value };
    for (const facet of facets) {
      if (facet.removable !== false) {
        next[facet.id] = undefined;
      }
    }
    onChange(next);
    dismiss();
  }

  function onPanelKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (event.key === "Escape") {
      // Escape at the value step steps back to the facet list; only the facet
      // step falls through to Popover's dismiss + focus return.
      if (openFacetId !== null) {
        event.preventDefault();
        setOpenFacetId(null);
      }
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>("[data-filter-item]") ??
        [],
    );
    if (items.length === 0) {
      return;
    }
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next =
      current < 0
        ? step === 1
          ? 0
          : items.length - 1
        : (current + step + items.length) % items.length;
    items[next]?.focus();
  }

  function toggleOption(facet: FilterFacet, optionValue: string): void {
    if (facet.kind === "dateRange") {
      return;
    }
    const selected = selectionOf(value, facet.id);
    const isSelected = selected.includes(optionValue);
    if (facet.kind === "single") {
      setFacet(facet.id, isSelected ? undefined : [optionValue]);
      return;
    }
    const next = isSelected
      ? selected.filter((entry) => entry !== optionValue)
      : [...selected, optionValue];
    setFacet(facet.id, next.length > 0 ? next : undefined);
  }

  // Silent while nothing is applied: an unfiltered list has nothing to report.
  const announcement =
    appliedCount > 0 && resultCount !== undefined
      ? formatResultCount(resultCount)
      : "";

  return (
    <div className="marker-filter-bar">
      <div className="marker-popover-anchor">
        <Button
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          ref={triggerRef}
          size="sm"
          variant="secondary"
          onClick={() => {
            if (isOpen) {
              dismiss();
              return;
            }
            setOpenFacetId(null);
            setIsOpen(true);
          }}
        >
          {appliedCount > 0 ? `Filter (${String(appliedCount)})` : "Filter"}
        </Button>
        <Popover
          anchorRef={triggerRef}
          aria-label="Filter"
          isOpen={isOpen}
          onDismiss={dismiss}
          onKeyDown={onPanelKeyDown}
        >
          <div className="marker-filter-bar__panel" ref={panelRef}>
            {openFacet === null ? (
              <div className="marker-filter-bar__list">
                {facets.map((facet) => (
                  <button
                    className="marker-filter-bar__item"
                    data-filter-item=""
                    key={facet.id}
                    onClick={() => {
                      setOpenFacetId(facet.id);
                    }}
                    type="button"
                  >
                    <span className="marker-filter-bar__item-label">
                      {facet.label}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="marker-filter-bar__panel-header">
                  <button
                    className="marker-filter-bar__back"
                    data-filter-item=""
                    onClick={() => {
                      setOpenFacetId(null);
                    }}
                    type="button"
                  >
                    Back
                  </button>
                  <Text as="span" variant="caps">
                    {openFacet.label}
                  </Text>
                </div>
                {openFacet.kind === "dateRange" ? (
                  <DateRangePicker
                    label={openFacet.label}
                    onChange={(range) => {
                      setFacet(
                        openFacet.id,
                        range.start === null && range.end === null
                          ? undefined
                          : range,
                      );
                    }}
                    value={rangeOf(value, openFacet.id)}
                  />
                ) : openFacet.options.length === 0 ? (
                  <Text variant="muted">No values.</Text>
                ) : (
                  <div className="marker-filter-bar__list">
                    {openFacet.options.map((option) => {
                      const isSelected = selectionOf(
                        value,
                        openFacet.id,
                      ).includes(option.value);
                      return (
                        <button
                          aria-pressed={isSelected}
                          className="marker-filter-bar__item"
                          data-filter-item=""
                          key={option.value}
                          onClick={() => {
                            toggleOption(openFacet, option.value);
                          }}
                          type="button"
                        >
                          <span className="marker-filter-bar__item-label">
                            {option.label}
                          </span>
                          {option.count === undefined ? null : (
                            <span className="marker-filter-bar__item-count">
                              {String(option.count)}
                            </span>
                          )}
                          <span className="marker-filter-bar__item-check">
                            {isSelected ? <CheckIcon /> : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </Popover>
      </div>
      {help}
      {appliedFacets.map((facet) => {
        const removable = facet.removable !== false;
        const text = chipText(facet, value);
        return (
          <span className="marker-filter-bar__chip" key={facet.id}>
            {removable ? (
              <button
                className="marker-filter-bar__chip-label"
                onClick={() => {
                  editFacet(facet.id);
                }}
                type="button"
              >
                {text}
              </button>
            ) : (
              <span className="marker-filter-bar__chip-label">{text}</span>
            )}
            {removable ? (
              <button
                aria-label={`Remove ${facet.label.toLocaleLowerCase()} filter`}
                className="marker-filter-bar__chip-remove"
                onClick={() => {
                  setFacet(facet.id, undefined);
                }}
                type="button"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            ) : null}
          </span>
        );
      })}
      {canClear ? (
        <Button size="sm" variant="secondary" onClick={clearAll}>
          Clear
        </Button>
      ) : null}
      {note ? (
        <Text as="span" className="marker-filter-bar__note" variant="muted">
          {note}
        </Text>
      ) : null}
      <div className="marker-filter-bar__status" role="status">
        {announcement}
      </div>
    </div>
  );
}
